import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import InquiryForm from "@/components/InquiryForm";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, TrendingUp, Clapperboard, Settings, ArrowRight, Globe,
  GraduationCap, Coins, CalendarCheck, Palette, Megaphone, Layout, BookPlus,
} from "lucide-react";

const services = [
  { icon: Palette, title: "Strategic Branding & Identity", items: ["Brand strategy & positioning", "Logo, palette & visual identity", "Voice, messaging & storytelling", "Competitive differentiation reports"] },
  { icon: Megaphone, title: "Digital Marketing & Growth", items: ["Social strategy & content calendars", "SEO & content roadmaps", "Email campaign design & automation", "Paid ads: Google, Meta, TikTok"] },
  { icon: Clapperboard, title: "Entertainment & Media", items: ["Artist & talent brand development", "Media kit & press packages", "Content production strategy", "Distribution & PR outreach"] },
  { icon: Settings, title: "Business Consulting & Operations", items: ["Business plans & pitch decks", "Workflow & systems design", "Revenue & monetization strategy", "Partnerships & remote teams"] },
  { icon: Layout, title: "Web & Digital Presence", items: ["Website strategy & UX planning", "E-commerce & funnel design", "Domain, hosting & platforms", "Setup & maintenance guidance"] },
  { icon: GraduationCap, title: "Educational & Training", items: ["Live webinars & workshops", "Self-paced course libraries", "1-on-1 coaching & mentorship", "Group masterminds & Q&A"] },
];

const whatWeDo = [
  { icon: Sparkles, t: "Build brands that stand out" },
  { icon: TrendingUp, t: "Marketing strategies that convert" },
  { icon: Clapperboard, t: "Navigate entertainment & media" },
  { icon: Settings, t: "Scale businesses with smart systems" },
];

const books = [
  {
    id: "marketing-playbook",
    title: "Marketing Playbook",
    subtitle: "Strategies for Startup Brands, Influencers, and Artists",
    price: "$29.99",
    cover: "https://customer-assets.emergentagent.com/job_strategy-hub-236/artifacts/y6nuspeg_image-1782797185943.png",
    blurb: "A tactical, no-fluff guide to building marketing momentum — from positioning and content to growth and analytics.",
  },
  {
    id: "digital-marketing",
    title: "How to Master Digital Marketing",
    subtitle: "For Startup Brands, Influencers, and Artists — A Step-by-Step Guide to Growing Your Brand Online",
    price: "$27.99",
    cover: "https://customer-assets.emergentagent.com/job_strategy-hub-236/artifacts/j2if9psb_image-1782797198555.png",
    blurb: "Your step-by-step roadmap to growing a brand online — social, SEO, paid, and the systems that scale.",
  },
  {
    id: "keys-to-consumers",
    title: "Keys to Consumers",
    subtitle: "Unlocking Buying Behavior for Startup Brands, Influencers, and Artists",
    price: "$24.99",
    cover: "https://customer-assets.emergentagent.com/job_strategy-hub-236/artifacts/3vu022c9_image-1782797810927.png",
    blurb: "Insightful strategies for understanding and influencing consumer decisions.",
  },
  {
    id: "impressions-branding",
    title: "Impressions and Branding Mastery",
    subtitle: "For Startup Brands, Influencers, and Artists",
    price: "$24.99",
    cover: "https://customer-assets.emergentagent.com/job_strategy-hub-236/artifacts/5znglg18_image-1782797786001.png",
    blurb: "Master first impressions and craft a brand identity that commands attention and trust.",
  },
  {
    id: "tips-secrets",
    title: "Tips and Secrets to Explosive Marketing Success",
    subtitle: "For Startup Brands, Influencers, and Artists",
    price: "$19.99",
    cover: "https://customer-assets.emergentagent.com/job_strategy-hub-236/artifacts/3g97kfls_image-1782797826725.png",
    blurb: "Proven tips and insider secrets to ignite rapid, sustainable marketing growth.",
  },
];

const bundles = [
  { id: "bundle-2", name: "Any 2 Books", price: "$47", save: "Save ~20%", featured: false },
  { id: "bundle-3", name: "Any 3 Books", price: "$67", save: "Save ~25%", featured: true },
];

const different = [
  { icon: CalendarCheck, t: "Free consultations for start-ups", d: "We meet emerging founders where they are — no cost to begin." },
  { icon: Sparkles, t: "Appointment-only strategy for established brands", d: "Focused, high-touch engagements for brands already in motion." },
  { icon: Globe, t: "100% online — no borders, no limits", d: "Video, cloud collaboration & digital deliverables, worldwide." },
  { icon: GraduationCap, t: "Free courses for members", d: "Business fundamentals, branding & marketing education included." },
  { icon: Coins, t: "Real investment avenues", d: "For owners ready to grow, we open doors to capital & partners." },
];

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("in")),
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

export default function Landing() {
  useReveal();
  const [buying, setBuying] = useState(null);
  const [salesEnabled, setSalesEnabled] = useState(false);

  useEffect(() => {
    api.get("/config").then((r) => setSalesEnabled(r.data.sales_enabled)).catch(() => {});
  }, []);

  const buyProduct = async (productId) => {
    if (!salesEnabled) {
      toast.info("Books are launching soon — checkout isn't open yet.");
      return;
    }
    setBuying(productId);
    try {
      const res = await api.post("/checkout/session", {
        product_id: productId,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch {
      toast.error("Couldn't start checkout. Please try again.");
      setBuying(null);
    }
  };

  return (
    <div className="grain bg-[#0B0C10] min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-screen flex items-end pt-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://customer-assets.emergentagent.com/job_strategy-hub-236/artifacts/vmzgmnzc_Messenger_creation_F0CDC270-E222-4802-BB51-C66F641BC0BA.webp"
            alt="Martin Ramnarine LLC — Solutions that move your business forward" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[#0B0C10] to-transparent" />
        </div>
        <div className="relative z-10 px-6 md:px-12 lg:px-24 w-full pb-16">
          <div className="fade-up flex flex-col sm:flex-row gap-4 justify-center" style={{ animationDelay: "0.4s" }}>
            <a href="#inquiry" className="bg-[#D4AF37] text-[#0B0C10] font-medium hover:bg-[#F1D570] transition-colors rounded-sm px-8 py-4 flex items-center justify-center gap-2 group" data-testid="hero-cta-button">
              Book Your Free Consultation
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <Link to="/dashboard" className="border border-[#3a3e4c] bg-[#0B0C10]/60 backdrop-blur text-white hover:border-[#D4AF37] transition-colors rounded-sm px-8 py-4 text-center" data-testid="hero-join-button">
              Join the Community
            </Link>
          </div>
        </div>
      </section>

      {/* WHAT WE DO ticker */}
      <section className="relative z-10 border-y border-[#1F222E] bg-[#0B0C10]">
        <div className="px-6 md:px-12 lg:px-24 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {whatWeDo.map((w, i) => (
            <div key={i} className="reveal flex items-start gap-4" style={{ transitionDelay: `${i * 0.08}s` }}>
              <w.icon className="text-[#D4AF37] shrink-0 mt-1" size={22} />
              <span className="text-sm text-white leading-snug">{w.t}</span>
            </div>
          ))}
        </div>
      </section>

      {/* MISSION */}
      <section id="mission" className="relative z-10 py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-4 reveal">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Our Mission</p>
            <div className="gold-line h-px w-24 mt-6" />
            <div className="mt-8 overflow-hidden border border-[#1F222E]">
              <img src="https://customer-assets.emergentagent.com/job_strategy-hub-236/artifacts/ptnrcqox_Messenger_creation_8AB6E702-4BCB-4C2D-B9DC-7E2F05730DC5.webp" alt="Strategy. Branding. Real Impact." className="w-full h-auto hover:scale-105 transition-transform duration-700" data-testid="mission-image" />
            </div>
          </div>
          <div className="md:col-span-8 reveal">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white font-light leading-tight">
              We bridge the gap between vision and execution.
            </h2>
            <p className="text-[#9CA3AF] mt-8 leading-relaxed text-lg">
              Martin Ramnarine LLC is a full-service consulting firm dedicated to empowering entrepreneurs, creators, and business owners with the strategic tools, branding expertise, and media solutions they need to build, grow, and sustain successful ventures. We cultivate a community of informed, empowered business leaders through accessible education, strategic consulting, and media services that drive measurable results.
            </p>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="relative z-10 py-24 md:py-32 px-6 md:px-12 lg:px-24 border-t border-[#1F222E]">
        <div className="reveal max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">What We Offer</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white font-light mt-5">
            Services, delivered entirely online.
          </h2>
          <p className="text-[#9CA3AF] mt-5">Every engagement is handled remotely via video, email, and cloud collaboration — no borders, no limits.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1F222E] border border-[#1F222E] mt-14">
          {services.map((s, i) => (
            <div key={i} className="reveal bg-[#12141A] p-8 hover:bg-[#16181f] transition-colors group" style={{ transitionDelay: `${(i % 3) * 0.08}s` }} data-testid={`service-card-${i}`}>
              <s.icon className="text-[#D4AF37]" size={28} />
              <h3 className="font-display text-xl text-white mt-6">{s.title}</h3>
              <ul className="mt-5 space-y-3">
                {s.items.map((it) => (
                  <li key={it} className="text-sm text-[#9CA3AF] flex gap-3">
                    <span className="text-[#D4AF37] mt-1">—</span>{it}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT'S DIFFERENT */}
      <section id="different" className="relative z-10 py-24 md:py-32 px-6 md:px-12 lg:px-24 border-t border-[#1F222E]">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4 reveal">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">What's Different</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white font-light mt-5 leading-tight">
              We meet you<br />where you are.
            </h2>
            <p className="text-[#9CA3AF] mt-6 leading-relaxed">
              Whether you're sketching your first idea on a napkin or scaling a brand already in motion.
            </p>
          </div>
          <div className="md:col-span-8 space-y-px bg-[#1F222E] border border-[#1F222E]">
            {different.map((d, i) => (
              <div key={i} className="reveal bg-[#12141A] p-7 flex items-start gap-5 hover:bg-[#16181f] transition-colors" style={{ transitionDelay: `${i * 0.06}s` }} data-testid={`different-${i}`}>
                <d.icon className="text-[#D4AF37] shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="text-white font-medium">{d.t}</h3>
                  <p className="text-sm text-[#9CA3AF] mt-1.5">{d.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATEMENT BANNER */}
      <section className="relative z-10 h-[60vh] min-h-[420px] flex items-center justify-center overflow-hidden border-t border-[#1F222E]" data-testid="statement-banner">
        <div className="absolute inset-0 z-0">
          <img src="https://customer-assets.emergentagent.com/job_strategy-hub-236/artifacts/2q44ou6u_18168.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0B0C10]/75" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C10] via-transparent to-[#0B0C10]" />
        </div>
        <div className="relative z-10 text-center px-6 max-w-3xl reveal">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37]">The Standard</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white font-light mt-6 leading-tight">
            Performance, precision, and presence — the standard we bring to every brand we build.
          </h2>
          <a href="#inquiry" className="inline-flex items-center gap-2 mt-8 text-sm text-white hover:text-[#D4AF37] transition-colors group" data-testid="banner-cta">
            Start your engagement <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </section>

      {/* BOOKS */}
      <section id="books" className="relative z-10 py-24 md:py-32 px-6 md:px-12 lg:px-24 border-t border-[#1F222E]">
        <div className="reveal max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Published Works</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white font-light mt-5">
            Books by Martin Ramnarine LLC.
          </h2>
          <p className="text-[#9CA3AF] mt-5">Practical playbooks distilling years of branding and marketing strategy into step-by-step guidance.</p>
        </div>
        {!salesEnabled && (
          <div className="mt-8 border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-6 py-4 rounded-sm flex items-center gap-3" data-testid="presale-banner">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Launching Soon</span>
            <span className="text-sm text-[#9CA3AF]">The digital editions are being finalized — purchasing opens shortly. Join the community to be notified the moment they drop.</span>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#1F222E] border border-[#1F222E] mt-8">
          {books.map((b, i) => (
            <div key={i} className="reveal bg-[#12141A] p-8 md:p-10 flex flex-col sm:flex-row gap-8 hover:bg-[#16181f] transition-colors" style={{ transitionDelay: `${i * 0.1}s` }} data-testid={`book-card-${i}`}>
              <div className="shrink-0 w-40 mx-auto sm:mx-0">
                <img src={b.cover} alt={b.title} className="w-full rounded-sm shadow-2xl border border-[#1F222E]" data-testid={`book-cover-${i}`} />
              </div>
              <div className="flex flex-col">
                <h3 className="font-display text-2xl text-white leading-snug">{b.title}</h3>
                <p className="text-sm text-[#D4AF37] mt-2">{b.subtitle}</p>
                <p className="text-[#9CA3AF] mt-4 leading-relaxed text-sm">{b.blurb}</p>
                <div className="mt-auto pt-6 flex items-center justify-between gap-4">
                  <span className="font-display text-2xl text-white" data-testid={`book-price-${i}`}>{b.price}</span>
                  <button onClick={() => buyProduct(b.id)} disabled={buying === b.id || !salesEnabled}
                    className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0B0C10] font-medium hover:bg-[#F1D570] transition-colors rounded-sm px-5 py-2.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed" data-testid={`book-cta-${i}`}>
                    {!salesEnabled ? "Coming Soon" : buying === b.id ? "Starting…" : "Buy now"} {salesEnabled && <ArrowRight size={16} />}
                  </button>
                </div>
              </div>
            </div>
          ))}
          {/* Coming soon placeholder — fills the empty grid cell beside the last book */}
          <div className="reveal bg-[#0F1116] p-8 md:p-10 flex flex-col items-center justify-center text-center border border-dashed border-[#2a2e3c]" data-testid="coming-soon-card">
            <div className="h-14 w-14 grid place-items-center border border-[#D4AF37]/50 text-[#D4AF37] rounded-full">
              <BookPlus size={26} />
            </div>
            <h3 className="font-display text-2xl text-white mt-6">More titles on the way.</h3>
            <p className="text-sm text-[#9CA3AF] mt-3 max-w-xs">New books are being added to the library. Join the community to be the first to know when they drop.</p>
            <Link to="/login" className="mt-6 inline-flex items-center gap-2 text-sm text-[#D4AF37] hover:text-[#F1D570] transition-colors group" data-testid="coming-soon-cta">
              Get notified <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
        <div className="mt-20 reveal border border-[#D4AF37]/30 bg-gradient-to-br from-[#16181f] to-[#0B0C10] p-8 md:p-12" data-testid="collection-section">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="flex items-end justify-center lg:justify-start -space-x-6">
                {books.map((b, i) => (
                  <img key={i} src={b.cover} alt={b.title}
                    className="w-24 sm:w-28 rounded-sm shadow-2xl border border-[#1F222E] transition-transform duration-300 hover:-translate-y-3 hover:z-10"
                    style={{ transform: `rotate(${(i - 2) * 3}deg)`, zIndex: i }}
                    data-testid={`collection-cover-${i}`} />
                ))}
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="border border-[#D4AF37]/40 bg-[#0B0C10] p-8 rounded-sm">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">The Complete Collection</p>
                <h3 className="font-display text-3xl text-white font-light mt-3 leading-tight">All 5 books. One library.</h3>
                <p className="text-[#9CA3AF] mt-4 text-sm leading-relaxed">The entire Martin Ramnarine LLC marketing series — branding, digital marketing, consumer psychology, impressions, and insider growth secrets — together in one complete set.</p>
                <div className="flex items-baseline gap-3 mt-6">
                  <span className="font-display text-5xl text-white" data-testid="collection-price">$125</span>
                  <span className="text-sm text-[#6B7280] line-through">$127.95</span>
                </div>
                <button onClick={() => buyProduct("bundle-5")} disabled={buying === "bundle-5" || !salesEnabled}
                  className="mt-6 w-full bg-[#D4AF37] text-[#0B0C10] font-semibold hover:bg-[#F1D570] transition-colors rounded-sm px-6 py-4 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="collection-buy">
                  {!salesEnabled ? "Coming Soon" : buying === "bundle-5" ? "Starting…" : "Get the Complete Set"} {salesEnabled && <ArrowRight size={18} />}
                </button>
                <p className="text-xs text-[#6B7280] mt-4 text-center">{salesEnabled ? "Secure card checkout via Stripe." : "Launching soon — join the community to be notified."}</p>
              </div>
            </div>
          </div>
        </div>

        {/* BUNDLES */}
        <div className="mt-20 reveal">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Save with Bundles</p>
          <h3 className="font-display text-2xl sm:text-3xl text-white font-light mt-4">Prefer a smaller bundle?</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#1F222E] border border-[#1F222E] mt-8">
            {bundles.map((bundle, i) => (
              <div key={i} className={`p-8 flex flex-col ${bundle.featured ? "bg-[#16181f] ring-1 ring-inset ring-[#D4AF37]/40" : "bg-[#12141A]"}`} data-testid={`bundle-card-${i}`}>
                {bundle.featured && <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-3">Best Value</span>}
                <h4 className="font-display text-xl text-white leading-snug">{bundle.name}</h4>
                <p className="font-display text-4xl text-white mt-4">{bundle.price}</p>
                <p className="text-sm text-[#9CA3AF] mt-2">{bundle.save}</p>
                <button onClick={() => buyProduct(bundle.id)} disabled={buying === bundle.id || !salesEnabled}
                  className="mt-auto pt-6 inline-flex items-center gap-2 text-sm text-[#D4AF37] hover:text-[#F1D570] transition-colors group disabled:opacity-50 disabled:cursor-not-allowed" data-testid={`bundle-cta-${i}`}>
                  {!salesEnabled ? "Coming Soon" : buying === bundle.id ? "Starting…" : "Buy the bundle"} {salesEnabled && <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />}
                </button>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#6B7280] mt-6">{salesEnabled ? "Secure checkout by card via Stripe. Digital editions are launching soon — your download will be emailed the moment it's ready." : "Purchasing opens once the digital editions are ready."}</p>
        </div>

        {/* PAYMENT — CASH APP (only when sales are open) */}
        {salesEnabled && (
        <div id="payment" className="mt-20 reveal border border-[#1F222E] bg-[#12141A] p-8 md:p-12" data-testid="cashapp-section">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Prefer Cash App?</p>
              <h3 className="font-display text-2xl sm:text-3xl text-white font-light mt-4">Pay directly via Cash App.</h3>
              <p className="text-[#9CA3AF] mt-4 leading-relaxed">
                In addition to card checkout above, you can send payment to the Cash App below, then email your receipt and the title(s) you'd like to <a href="mailto:mdr@martinramnarinellc.com" className="text-[#D4AF37] hover:text-[#F1D570]">mdr@martinramnarinellc.com</a>. We'll confirm your order and deliver as soon as the digital editions are ready.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <span className="font-display text-3xl text-white" data-testid="cashapp-handle">$MARTINRAMNARINE</span>
                <a href="https://cash.app/$MARTINRAMNARINE" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#00D632] text-[#0B0C10] font-semibold hover:bg-[#00e838] transition-colors rounded-sm px-7 py-3" data-testid="cashapp-button">
                  Open in Cash App <ArrowRight size={18} />
                </a>
              </div>
            </div>
            <div className="md:col-span-5 flex justify-center md:justify-end">
              <div className="bg-white p-4 rounded-lg">
                <img src="https://customer-assets.emergentagent.com/job_strategy-hub-236/artifacts/1odxmsyl_18287.jpg" alt="Cash App QR code for $MARTINRAMNARINE" className="w-56 h-56 object-contain" data-testid="cashapp-qr" />
              </div>
            </div>
          </div>
        </div>
        )}
      </section>

      {/* COMMUNITY */}
      <section id="community" className="relative z-10 overflow-hidden border-t border-[#1F222E]" data-testid="community-section">
        <div className="absolute inset-0 z-0">
          <img src="https://customer-assets.emergentagent.com/job_strategy-hub-236/artifacts/rr8uc9t9_18167.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0B0C10]/45" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B0C10] via-[#0B0C10]/55 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C10] via-transparent to-[#0B0C10]/80" />
        </div>
        <div className="relative z-10 px-6 md:px-12 lg:px-24 py-24 md:py-32 max-w-2xl reveal">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">The Community</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white font-light mt-5 leading-tight">
            Join a community of empowered business leaders.
          </h2>
          <p className="text-[#9CA3AF] mt-6 leading-relaxed text-lg">
            Members get free courses on branding, marketing, and business fundamentals, plus a network of founders, creators, and owners building from anywhere in the world.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mt-10">
            <Link to="/login" className="bg-[#D4AF37] text-[#0B0C10] font-medium hover:bg-[#F1D570] transition-colors rounded-sm px-8 py-4 flex items-center justify-center gap-2 group" data-testid="community-join-button">
              Join the Community
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/dashboard" className="border border-[#1F222E] text-white hover:border-[#D4AF37] transition-colors rounded-sm px-8 py-4 text-center" data-testid="community-courses-button">
              Browse Free Courses
            </Link>
          </div>
        </div>
      </section>

      {/* INQUIRY */}
      <section id="inquiry" className="relative z-10 py-24 md:py-32 px-6 md:px-12 lg:px-24 border-t border-[#1F222E]">
        <div className="max-w-3xl mx-auto text-center reveal mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Client Inquiry</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white font-light mt-5">
            Let's build something that lasts.
          </h2>
          <p className="text-[#9CA3AF] mt-5">Tell us about your vision. Start-ups receive a free consultation.</p>
        </div>
        <div className="max-w-4xl mx-auto reveal">
          <InquiryForm />
        </div>
      </section>

      <Footer />
    </div>
  );
}
